import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, HelpCircle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["Open", "Validated", "Invalidated"];
const statusCls = (st) => st === "Validated" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : st === "Invalidated" ? "text-red-700 bg-red-50 border-red-200"
  : "text-amber-700 bg-amber-50 border-amber-200";
const ragDot = (r) => r === "red" ? "bg-red-500" : r === "green" ? "bg-emerald-500" : "bg-amber-500";
const inputCls = "border border-zinc-300 rounded px-2 py-1 text-[12px] bg-white";
const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center"><div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div></div>);

function AddForm({ onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const empty = { assumption: "", owner: "", impact_if_wrong: "", validate_by: "", evidence: "" };
  const [f, setF] = useState(empty);
  const submit = async () => {
    if (!f.assumption.trim()) { toast.error("Enter an assumption"); return; }
    await onAdd(f); setF(empty); setOpen(false);
  };
  if (!open) return (
    <button data-testid="assumption-add-toggle" onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5"><Plus size={14} /> Add assumption</button>
  );
  return (
    <div data-testid="assumption-add-form" className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 space-y-2">
      <input data-testid="assumption-add-text" className={`${inputCls} w-full`} placeholder="Assumption (e.g. Suppliers adopt the portal within 60 days)" value={f.assumption} onChange={(e) => setF({ ...f, assumption: e.target.value })} />
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid="assumption-add-owner" className={inputCls} placeholder="Owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        <input data-testid="assumption-add-validateby" type="date" className={inputCls} value={f.validate_by} onChange={(e) => setF({ ...f, validate_by: e.target.value })} title="Validate by" />
        <input data-testid="assumption-add-impact" className={inputCls} placeholder="Impact if wrong" value={f.impact_if_wrong} onChange={(e) => setF({ ...f, impact_if_wrong: e.target.value })} />
      </div>
      <div className="flex gap-2">
        <button data-testid="assumption-add-submit" onClick={submit} disabled={busy} className="text-[12px] tracking-[0.12em] uppercase bg-[var(--e360-primary)] text-white px-4 py-1.5 rounded disabled:opacity-40">Add</button>
        <button onClick={() => setOpen(false)} className="text-[12px] tracking-[0.12em] uppercase text-zinc-500 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

export default function AssumptionRegister({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/assumptions`); setData(data); }
    catch { toast.error("Could not load the assumption register"); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const withBusy = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setData(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const add = (b) => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/assumptions`, b), "Assumption added");
  const save = (id, b) => withBusy(() => api.put(`/admin/enterprise360/clients/${clientId}/assumptions/${id}`, b));
  const del = (id) => { if (!window.confirm("Delete this assumption?")) return; withBusy(() => api.delete(`/admin/enterprise360/clients/${clientId}/assumptions/${id}`), "Removed"); };

  const s = data?.summary || {};
  const items = data?.items || [];
  return (
    <div data-testid="assumption-register">
      <p className="text-[12px] text-zinc-500 max-w-xl mb-3"><HelpCircle size={12} className="inline mr-1" /> Every assumption gets an owner and a validate-by date. Any still open past its date is auto-flagged into the early-warning engine — no silent killers.</p>
      <div data-testid="assumption-summary" className="flex items-center gap-6 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
        <Stat label="Open" value={s.open ?? 0} cls={s.open ? "text-amber-600" : "text-black"} />
        <Stat label="Validated" value={s.validated ?? 0} cls="text-emerald-600" />
        <Stat label="Invalidated" value={s.invalidated ?? 0} cls={s.invalidated ? "text-red-600" : "text-black"} />
        <Stat label="Overdue" value={s.overdue ?? 0} cls={s.overdue ? "text-red-600" : "text-black"} />
        <Stat label="Total" value={s.total ?? 0} cls="text-zinc-400" />
      </div>
      <div className="mb-3"><AddForm onAdd={add} busy={busy} /></div>
      {loading ? <div className="text-sm text-zinc-400 py-6 text-center">Loading…</div>
        : items.length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center">No assumptions logged yet.</div>
        : <div className="border border-zinc-200 rounded-lg divide-y" data-testid="assumption-list">
            {items.map((it) => (
              <div key={it.id} data-testid={`assumption-row-${it.id}`} className="px-3 py-2.5 flex items-start gap-3 flex-wrap">
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-none ${ragDot(it.rag)}`} />
                <div className="flex-1 min-w-[240px]">
                  <div className="text-[13px] text-zinc-800 font-medium">{it.assumption}</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">{it.ref} · Owner: {it.owner || "—"} · Validate by: {it.validate_by ? new Date(it.validate_by).toLocaleDateString("en-GB") : "—"}{it.impact_if_wrong ? ` · Impact if wrong: ${it.impact_if_wrong}` : ""}</div>
                </div>
                <select data-testid={`assumption-status-${it.id}`} className={`${inputCls} font-semibold ${statusCls(it.status)}`} value={it.status} disabled={busy}
                  onChange={(e) => save(it.id, { status: e.target.value })}>
                  {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
                <button data-testid={`assumption-delete-${it.id}`} onClick={() => del(it.id)} disabled={busy} className="text-zinc-300 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>}
    </div>
  );
}
